import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CurrentUser } from '@appTypes/api';
import {
  tokenStorage,
  normalizePermissions,
  normalizeHiddenSections,
  type LoginResponse,
  type RawPermissions,
} from '@lib/auth/storage';

interface AuthState {
  user: CurrentUser | null;
  permissions: string[];
  /**
   * Untransformed permissions object as returned by the backend. Pages
   * that gate UI on RESTRICTIVE flags (see `permissionsCatalog.isRestriction`)
   * must read from here instead of `usePermission()` so the admin-role
   * bypass cannot accidentally apply the restriction to admins.
   */
  rawPermissions: RawPermissions | null;
  /**
   * Nav-section IDs (see `navConfig.NAV_ITEMS[].id`) hidden from the sidebar
   * for this sub-user. An INDEPENDENT layer from `permissions` — the sidebar
   * hides a leaf when its id is listed here even if the user is permitted to
   * see it. Defaults to `[]` (nothing hidden = current behaviour).
   */
  hiddenMenuSections: string[];
  isAuthenticated: boolean;
  hydrated: boolean;
  setUser: (user: CurrentUser | null) => void;
  setPermissions: (perms: string[]) => void;
  loginSuccess: (resp: LoginResponse, user?: CurrentUser) => void;
  logout: () => void;
  hydrate: () => void;
}

function toRawPermissions(input: unknown): RawPermissions | null {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    return input as RawPermissions;
  }
  return null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      permissions: [],
      rawPermissions: null,
      hiddenMenuSections: [],
      isAuthenticated: false,
      hydrated: false,

      setUser: (user) => set({ user }),
      setPermissions: (permissions) => set({ permissions }),

      loginSuccess: (resp, user) => {
        tokenStorage.store(resp);
        set({
          user: user ?? null,
          permissions: normalizePermissions(resp.permissions),
          rawPermissions: toRawPermissions(resp.permissions),
          hiddenMenuSections: normalizeHiddenSections(resp.menu_hidden_sections),
          isAuthenticated: true,
          hydrated: true,
        });
      },

      logout: () => {
        tokenStorage.clear();
        set({
          user: null,
          permissions: [],
          rawPermissions: null,
          hiddenMenuSections: [],
          isAuthenticated: false,
        });
      },

      hydrate: () => {
        // On cold boot, Zustand `persist` may already have rehydrated `user`
        // from localStorage with whoever was logged in last. If there is no
        // valid access token, that persisted identity is stale (e.g. the
        // previous session expired, or the user logged out from another tab)
        // and must be wiped — otherwise the Header would briefly display the
        // wrong name when a different user logs in next.
        const hasToken = !!tokenStorage.getAccessToken();
        set((state) => ({
          user: hasToken ? state.user : null,
          isAuthenticated: hasToken,
          permissions: hasToken ? tokenStorage.getPermissions() : [],
          rawPermissions: hasToken ? tokenStorage.getRawPermissions() : null,
          hiddenMenuSections: hasToken ? tokenStorage.getHiddenMenuSections() : [],
          hydrated: true,
        }));
      },
    }),
    {
      name: 'timmy-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ user: s.user }),
    },
  ),
);
